import * as React from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Input, Checkbox, RadioGroup } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { useActiveBranches } from "services/branchService";
import SubmitButton from "components/common/SubmitButton";

const EditBillingTemplate = ({ cancelEdit, onSubmit, isLoading, item }) => {
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const { data: branches, loading: branchesLoading } = useActiveBranches();

  const getInitialDefaultOption = () => {
    if (!item) return "notDefault";
    if (item.defaultBranchId != null && item.defaultBranchId !== undefined) {
      return "specificBranch";
    }
    if (item.default === true) return "allBranches";
    return "notDefault";
  };

  const [defaultOption, setDefaultOption] = React.useState(
    getInitialDefaultOption()
  );

  React.useEffect(() => {
    setDefaultOption(getInitialDefaultOption());
  }, [item?.default, item?.defaultBranchId]);

  const handleRadioChange = React.useCallback((e, formRenderProps) => {
    try {
      if (!e || e === null) {
        return;
      }
      const newValue = e.value;
      if (newValue === undefined || newValue === null) {
        return;
      }
      setDefaultOption(newValue);
      
      if (formRenderProps?.onChange) {
        formRenderProps.onChange("defaultOption", newValue);
        
        if (newValue !== "specificBranch") {
          formRenderProps.onChange("defaultBranch", null);
        }
      }
    } catch (error) {
      console.error("Error in RadioGroup onChange:", error);
    }
  }, []);

  const validateForm = (values) => {
    const errors = {};
    if (!values.templateName || values.templateName.trim() === "") {
      errors.templateName = "Template name is required";
    }
    if (defaultOption === "specificBranch" && !values.defaultBranch) {
      errors.defaultBranch = "Branch is required when selecting default for branch";
    }
    return errors;
  };

  const handleFormSubmit = (formData) => {
    let submitData = { ...formData };

    if (defaultOption === "notDefault") {
      submitData.default = null;
      submitData.defaultBranchId = null;
      submitData.defaultBranchName = null;
    } else if (defaultOption === "allBranches") {
      submitData.default = true;
      submitData.defaultBranchId = null;
      submitData.defaultBranchName = null;
    } else if (defaultOption === "specificBranch") {
      submitData.default = null;
      submitData.defaultBranchId = formData.defaultBranch?.branchId || null;
      submitData.defaultBranchName = formData.defaultBranch?.branchName || null;
    }

    delete submitData.defaultBranch;

    onSubmit(submitData);
  };

  const initialValues = React.useMemo(() => {
    const baseValues = {
      active: item?.active ?? true,
      templateName: item?.templateName || "",
      ...item,
      defaultBranch: null,
    };
    
    if (item?.defaultBranchId && branches && branches.length > 0) {
      baseValues.defaultBranch = branches.find(
        (b) => b.branchId === item.defaultBranchId
      ) || null;
    }
    
    return baseValues;
  }, [item, branches]);


  return (
    <Dialog
      title={item ? "Edit Bill Template" : "Add Bill Template"}
      onClose={cancelEdit}
      width={600}
    >
      <Form
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validator={validateForm}
        render={(formRenderProps) => (
          <>
            <div className="p-3 ps-4">
              <FormElement style={{ maxWidth: 1200 }}>
                <fieldset className="k-form-fieldset">
                  <div className="mb-2">
                    <Label className="label mt-1 mb-2 w-100">
                      Template Name
                    </Label>
                    <Field
                      name="templateName"
                      component={Input}
                      placeholder="Enter template name"
                      style={{border: submitAttempted && formRenderProps.errors?.templateName ? "1px solid red" : ""}}
                    />
                   
                  </div>

                  <div className="mb-3 border rounded px-3 py-2 mt-3 shadow-sm">
                    <Label className="label mt-1 mb-2 w-100">
                      Default Templates
                    </Label>
                    <div className="d-flex align-items-end gap-2">
                      <Field name="defaultOption" component="input" type="hidden" />
                      <RadioGroup
                        data={[
                          { label: "Not Default template", value: "notDefault" },
                          {
                            label: "Default for all branches",
                            value: "allBranches",
                          },
                          {
                            label: "Default for branch",
                            value: "specificBranch",
                          },
                        ]}
                        value={defaultOption}
                        className="radio"
                        onChange={(e) => handleRadioChange(e, formRenderProps)}
                        layout="vertical"
                      />
                      {defaultOption === "specificBranch" && (
                        <div className="mt-3">
                          <Field
                            name="defaultBranch"
                            component={ComboBox}
                            data={branches || []}
                            textField="branchName"
                            dataItemKey="branchId"
                            placeholder={
                              branchesLoading
                                ? "Loading branches..."
                                : "Select Branch"
                            }
                            disabled={branchesLoading}
                            style={{ height: "32px", width: "210px",
                              border: submitAttempted && formRenderProps.errors?.defaultBranch ? "1px solid red" : "",
                             }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <Field name="active" component={Checkbox} label="Active" />
                </fieldset>
              </FormElement>
            </div>
            <div className="dialog-footer">
              <button
                className="bgBtnSecondary common-btn me-2"
                onClick={cancelEdit}
              >
                Cancel
              </button>
              <SubmitButton
                className="me-3"
                isLoading={isLoading}
                onClick={() => {
                  setSubmitAttempted(true);
                  formRenderProps.onSubmit();
                }}
                disabled={item ? !formRenderProps.modified : !formRenderProps.allowSubmit}
              >
                {item ? "Update" : "Save"}
              </SubmitButton>
            </div>
          </>
        )}
      />
    </Dialog>
  );
};

export default EditBillingTemplate;
